import { useCallback, useRef } from "react";
/**
 * Merge multiple refs into a callback ref that can be passed to `ref`.
 *
 * This React hook merges multiple refs into a single callback function.
 * It accepts an arbitrary number of arguments, each of which can be null,
 * undefined, a mutable ref object, or a ref callback function. It returns
 * a callback function that can be passed to a React component's ref
 * attribute.
 *
 * Example Usage:
 * ```
 * const mergedRef = useMergedRefs(ref1, ref2, ref3);
 * <div ref={mergedRef}></div>
 * ```
 */ export var useMergedRefs = function() {
    for(var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++){
        args[_key] = arguments[_key];
    }
    var mostRecentRef = useRef(null);
    var refArray = useRef(null);
    if (refArray.current && mostRecentRef.current) {
        var _iteratorNormalCompletion = true, _didIteratorError = false, _iteratorError = undefined;
        try {
            for(var _iterator = args[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true){
                var ref = _step.value;
                if (ref && !refArray.current.includes(ref)) {
                    if (typeof ref === "function") {
                        ref(mostRecentRef.current);
                    } else {
                        ref.current = mostRecentRef.current;
                    }
                }
            }
        } catch (err) {
            _didIteratorError = true;
            _iteratorError = err;
        } finally{
            try {
                if (!_iteratorNormalCompletion && _iterator.return != null) {
                    _iterator.return();
                }
            } finally{
                if (_didIteratorError) {
                    throw _iteratorError;
                }
            }
        }
        var _iteratorNormalCompletion1 = true, _didIteratorError1 = false, _iteratorError1 = undefined;
        try {
            for(var _iterator1 = refArray.current[Symbol.iterator](), _step1; !(_iteratorNormalCompletion1 = (_step1 = _iterator1.next()).done); _iteratorNormalCompletion1 = true){
                var ref1 = _step1.value;
                if (ref1 && !args.includes(ref1)) {
                    if (typeof ref1 === "function") {
                        ref1(null);
                    } else {
                        ref1.current = null;
                    }
                }
            }
        } catch (err) {
            _didIteratorError1 = true;
            _iteratorError1 = err;
        } finally{
            try {
                if (!_iteratorNormalCompletion1 && _iterator1.return != null) {
                    _iterator1.return();
                }
            } finally{
                if (_didIteratorError1) {
                    throw _iteratorError1;
                }
            }
        }
    }
    refArray.current = args;
    return useCallback(function(value) {
        mostRecentRef.current = value;
        if (refArray.current) {
            var _iteratorNormalCompletion = true, _didIteratorError = false, _iteratorError = undefined;
            try {
                for(var _iterator = refArray.current[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true){
                    var ref = _step.value;
                    if (ref) {
                        if (typeof ref === "function") {
                            ref(value);
                        } else {
                            ref.current = value;
                        }
                    }
                }
            } catch (err) {
                _didIteratorError = true;
                _iteratorError = err;
            } finally{
                try {
                    if (!_iteratorNormalCompletion && _iterator.return != null) {
                        _iterator.return();
                    }
                } finally{
                    if (_didIteratorError) {
                        throw _iteratorError;
                    }
                }
            }
        }
    }, []);
};
